import React from 'react';
import {motion} from 'framer-motion';
import {useAuth} from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiTrendingUp, FiUsers, FiCalendar, FiBookOpen, FiMessageCircle, FiStar, FiActivity} = FiIcons;

const Dashboard = () => {
  const {user} = useAuth();

  const stats = [
    {
      icon: FiBookOpen,
      label: 'Modules Completed',
      value: 12,
      change: '+3 this week',
      color: 'text-blue-500'
    },
    {
      icon: FiCalendar,
      label: 'Events Attended',
      value: 8,
      change: '+2 this month',
      color: 'text-green-500'
    },
    {
      icon: FiUsers,
      label: 'Groups Joined',
      value: 5,
      change: '+1 this week',
      color: 'text-purple-500'
    },
    {
      icon: FiMessageCircle,
      label: 'Forum Posts',
      value: 23,
      change: '+7 this week',
      color: 'text-orange-500'
    }
  ];

  const recentActivity = [
    {
      type: 'learning',
      title: 'Completed "Understanding the Constitution"',
      time: '2 hours ago',
      icon: FiBookOpen
    },
    {
      type: 'event',
      title: 'Attended Conservative Leadership Summit',
      time: '1 day ago',
      icon: FiCalendar
    },
    {
      type: 'community',
      title: 'Joined Small Business Owners group',
      time: '3 days ago',
      icon: FiUsers
    },
    {
      type: 'forum',
      title: 'Posted in "Local Election Strategy"',
      time: '5 days ago',
      icon: FiMessageCircle
    }
  ];

  const upcomingEvents = [
    {
      title: 'Young Conservatives Meetup',
      date: 'March 22',
      location: 'Miami, FL'
    },
    {
      title: 'Candidate Forum: Local Elections',
      date: 'March 25',
      location: 'Denver, CO'
    },
    {
      title: 'Freedom Fundraiser Gala',
      date: 'March 28',
      location: 'Nashville, TN'
    }
  ];

  const recommendedModules = [
    {
      title: 'Federalism and States\' Rights',
      duration: '35 min',
      difficulty: 'Intermediate'
    },
    {
      title: 'The American Revolution: Causes and Impact',
      duration: '50 min',
      difficulty: 'Beginner'
    },
    {
      title: 'Advanced Constitutional Law',
      duration: '90 min',
      difficulty: 'Advanced'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.6}}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-patriot-navy mb-2">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-gray-600">
            Here's what's happening in your conservative community today.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: index * 0.1}}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <SafeIcon icon={stat.icon} className={`h-8 w-8 ${stat.color}`} />
                <span className="text-2xl font-bold text-patriot-navy">{stat.value}</span>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">{stat.label}</h3>
              <p className="text-xs text-green-600">{stat.change}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Activity */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.2}}
              className="bg-white rounded-lg shadow-md p-6 mb-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-patriot-navy">Recent Activity</h2>
                <SafeIcon icon={FiActivity} className="h-5 w-5 text-gray-400" />
              </div>
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200">
                    <div className="flex-shrink-0">
                      <SafeIcon icon={activity.icon} className="h-5 w-5 text-patriot-red mt-0.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Recommended Learning */}
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.3}}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-patriot-navy">Recommended Learning</h2>
                <SafeIcon icon={FiBookOpen} className="h-5 w-5 text-gray-400" />
              </div>
              <div className="space-y-4">
                {recommendedModules.map((module, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-patriot-red transition-colors duration-200">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">{module.title}</h3>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                        {module.difficulty}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{module.duration}</span>
                      <button className="text-patriot-red hover:text-patriot-red-dark text-sm font-medium">
                        Start Learning →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upcoming Events */}
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.4}}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-patriot-navy">Upcoming Events</h2>
                <SafeIcon icon={FiCalendar} className="h-5 w-5 text-gray-400" />
              </div>
              <div className="space-y-4">
                {upcomingEvents.map((event, index) => (
                  <div key={index} className="border-l-4 border-patriot-red pl-4">
                    <h3 className="font-medium text-gray-900 text-sm">{event.title}</h3>
                    <p className="text-xs text-gray-600">{event.date} • {event.location}</p>
                  </div>
                ))}
              </div>
              <button className="w-full mt-4 text-patriot-red hover:text-patriot-red-dark text-sm font-medium">
                View All Events →
              </button>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.5}}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-xl font-semibold text-patriot-navy mb-6">Quick Actions</h2>
              <div className="space-y-3">
                <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex items-center">
                  <SafeIcon icon={FiBookOpen} className="h-4 w-4 mr-3 text-patriot-red" />
                  Continue Learning
                </button>
                <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex items-center">
                  <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-3 text-patriot-red" />
                  Find Events
                </button>
                <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex items-center">
                  <SafeIcon icon={FiUsers} className="h-4 w-4 mr-3 text-patriot-red" />
                  Join Groups
                </button>
              </div>
            </motion.div>

            {/* Achievement Spotlight */}
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.6}}
              className="bg-gradient-to-br from-patriot-red to-patriot-navy rounded-lg shadow-md p-6 text-white"
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiStar} className="h-6 w-6 text-patriot-gold mr-2" />
                <h2 className="text-lg font-semibold">Achievement Unlocked!</h2>
              </div>
              <h3 className="font-medium mb-2">Learning Champion</h3>
              <p className="text-sm text-gray-200">
                You've completed 10+ learning modules. Keep up the great work!
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;